<template>
  <div class="w-full h-full px-5 pt-[14px] pb-[22px] bg-gray-200/20 rounded-xl">
    <div class="flex items-center gap-x-2.5 mb-2">
      <Icon name="Text" :size="22" class="stroke-gray-800" />

      <div
        class="font-secondary font-medium text-[15px] leading-[20px] text-gray-800"
      >
        Transcript
      </div>
    </div>

    <div class="w-full h-px mb-4 bg-gray-200/50"></div>

    <MatchMarkers :markers="match.markers" />

    <div class="relative">
      <RouterLink :to="viewFullFileTo" class="group absolute right-0 z-10">
        <UiButton icon="Eye" class="bg-white opacity-30 group-hover:hidden" />

        <UiButton
          icon="Eye"
          class="hidden group-hover:flex bg-white hover:bg-white"
        >
          View full file
        </UiButton>
      </RouterLink>

      <div
        class="font-secondary text-[12px] text-gray-500 leading-[20px] mb-1.5"
      >
        Transcript
      </div>

      <div ref="container" class="font-secondary mb-[14px] relative">
        <div
          v-for="data in formattedTranscript"
          :key="data.timestamp"
          class="text-[14px] leading-[20px] flex relative"
          :data-group-id="data.groupId"
          :data-invisible="data.invisible || undefined"
          :style="{
            ...(data.highlight &&
              !data.invisible &&
              data.groupId && {
                marginBottom: `-${groupMargins[data.groupId] ?? 0}px`,
              }),
          }"
        >
          <div class="min-w-[90px] text-gray-500 tabular-nums">
            {{ data.timestamp }}
          </div>
          <div class="relative">
            <MatchTag
              v-if="data.highlight && !data.invisible"
              :id="number"
              :level="match.score ? match.level : undefined"
              class="absolute -top-3 -left-2"
            />
            <div
              :class="getClasses(data)"
              :style="{
                ...(data.highlight &&
                  !data.invisible && {
                    borderWidth: '1px 0',
                  }),
              }"
              v-html="sanitizeHtml(data.text)"
            ></div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { graphql } from '@/shared/graphql/router'

export const TranscriptMatchFragment = graphql(`
  fragment TranscriptMatchFields on TranscriptMatch {
    id
    location
    summary
    score
    level
    start
    end
    markers {
      color
      shine
      visible
    }
    transcript {
      timestamp
      text
      highlight
    }
  }
`)
</script>

<script setup lang="ts">
import {
  type FragmentType,
  useFragment,
} from '@/shared/graphql/router/fragment-masking'
import MatchMarkers from './MatchMarkers.vue'
import MatchTag from './matches/MatchTag.vue'
import { ScoreLevel, type Transcript } from '@/shared/graphql/router/graphql'
import { sanitizeHtml } from '@/shared/utils/sanitize'
import { Icon, UiButton } from '@dacr/ui'
import { computed, onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'

const props = defineProps<{
  match: FragmentType<typeof TranscriptMatchFragment>
  url?: string | null
  number: number
  side?: 'registered' | 'infringing'
  allowPlayback?: boolean
}>()

const match = useFragment(TranscriptMatchFragment, props.match)

const viewFullFileTo = computed(() => {
  const resolvedSide = props.side ?? (match.score ? 'infringing' : 'registered')
  return {
    path: `/infringement/report/${route.params.id}/transcript`,
    query: { side: resolvedSide },
  }
})

type FormattedTranscriptMatch = Transcript & {
  invisible: boolean
  groupId?: string
}

const route = useRoute()

const container = ref<HTMLElement>()
const groupMargins = ref<Record<string, number>>({})

const formattedTranscript = computed(() => {
  let currentHighlightGroup: FormattedTranscriptMatch | null = null
  let currentGroupId: string | null = null
  let groupCounter = 0

  return match.transcript.slice().reduce((acc, data) => {
    const item = { ...data } as FormattedTranscriptMatch

    if (item.highlight && currentHighlightGroup && currentGroupId) {
      item.invisible = true
      item.groupId = currentGroupId
      currentHighlightGroup.text += `<br />${item.text}`
    }

    if (item.highlight && !currentHighlightGroup) {
      currentGroupId = `g${groupCounter++}`
      item.groupId = currentGroupId
      currentHighlightGroup = item
    }

    if (!item.highlight) {
      currentHighlightGroup = null
      currentGroupId = null
    }

    acc.push(item)
    return acc
  }, [] as FormattedTranscriptMatch[])
})

const getClasses = (data: FormattedTranscriptMatch) => ({
  invisible: data.invisible,
  ...(!data.invisible && {
    'inline border outline-2 whitespace-pre-wrap rounded-[3px]': data.highlight,
    'border-[#e2e3e5] outline-gray-500 bg-[#e2e3e5]':
      !match.summary && data.highlight,
    ...(match.summary &&
      data.highlight && {
        'border-[#f5dbda] outline-red-500 bg-[#f5dbda]':
          match.level === ScoreLevel.High,
        'border-[#f5ebdc] outline-yellow-500 bg-[#f5ebdc]':
          match.level === ScoreLevel.Medium,
        'border-[#dceff3] outline-green-500 bg-[#dceff3]':
          match.level === ScoreLevel.Low,
      }),
  }),
})

const handleResize = () => {
  const margins: Record<string, number> = {}
  for (const el of Array.from(
    container.value?.querySelectorAll<HTMLElement>('[data-invisible]') ?? []
  )) {
    const id = el.dataset.groupId
    if (!id) continue
    margins[id] = (margins[id] ?? 0) + (el.clientHeight || 0)
  }
  groupMargins.value = margins
}

onMounted(() => {
  handleResize()
  window.addEventListener('resize', handleResize)
})
</script>
